/**
 * RENDER "WHY NO ROWS WERE WRITTEN" FROM A /mappings/populate RESPONSE — task-7c6r0ul7j1.
 *
 * ⛔ THE DEFECT THIS EXISTS FOR: `attribute_value_ranges` is 0 rows in production and nobody could
 *   say why. "The job never ran" and "every attribute failed a gate" are INDISTINGUISHABLE from the
 *   table. The server has always sent the reasons; the admin page rendered `step` and `created` and
 *   dropped `skipped`, `skipReasons`, `errors` and `errorReasons` on the floor.
 *
 * ⛔⛔ AND THE FIRST VERSION OF THIS FUNCTION DISCARDED `detail`/`reason` DELIBERATELY, WHICH WAS
 *   WRONG — codex-reviewer-2 blocked on it and was right. My stated reason was "detail restates
 *   required/saw as prose and would push the actionable numbers off a small screen". THAT IS TRUE
 *   OF EXACTLY ONE GATE. `min_usable_values` carries structured `required`/`saw`; it is the gate I
 *   had in front of me and I generalised from it.
 *   ⇒ For `type_indeterminate`, `min_parseable_numeric_values`, `no_value_above_5pct_frequency`,
 *   `direction_not_orderable`, `unsupported_data_type` and every SECTOR-WIDE gate
 *   (`no_categories_with_attributes`, `no_attributes_for_sector`, `required_table_missing`),
 *   `detail`/`reason` IS THE ONLY DIAGNOSTIC THAT EXISTS. Dropping it left the operator with a bare
 *   label — `weight: min_parseable_numeric_values` — which names the gate and explains nothing.
 *   🔑 A BOUNDED OBSERVATION PROMOTED TO A UNIVERSAL RULE, in the function whose whole job is
 *   explaining things. The numbers still come first; the prose now follows instead of vanishing.
 *
 * ⚠️ The server already caps `detail`/`reason` at 300 chars (routes/_skipSamples.js), so rendering
 *   them cannot unbound the output. The sample count is capped there too.
 */

export interface SkipSample {
  gate: string;
  attribute?: string;
  product_category?: string;
  required?: number | string;
  /** The service's own name for the observed count (MappingPopulationService:631). */
  saw?: number | string;
  detail?: string;
  reason?: string;
}

export interface PopulateStep {
  step: string;
  created?: number;
  candidates?: number;
  skipped?: number;
  skipReasons?: Record<string, number>;
  skipSamples?: SkipSample[];
  skipSampleCap?: number;
  skipSamplesTruncated?: boolean;
}

/** One sampled skip, as a single line: WHO, then the gate, then numbers, then prose. */
function renderSample(k: SkipSample): string {
  const who = [k.attribute, k.product_category].filter(Boolean).join(' / ');
  const nums = [
    k.required !== undefined ? `required=${k.required}` : null,
    k.saw !== undefined ? `saw=${k.saw}` : null,
  ]
    .filter(Boolean)
    .join(' ');
  // ⚠️ ORDER IS LOAD-BEARING: gate, then the measured numbers, then the sentence. An operator
  //    scanning twenty lines reads the left edge; the number that decides the case must not sit
  //    behind a clause. `detail` and `reason` are alternatives, never both on one record.
  const prose = k.detail ?? k.reason;
  return `    ${who || '(sector-wide)'}: ${k.gate}`
    + (nums ? ` ${nums}` : '')
    + (prose ? ` — ${prose}` : '');
}

/**
 * @returns a block to append to the operator message, or '' when nothing was skipped.
 *   Newlines are intentional: the caller MUST render with `whitespace-pre-wrap`, or every line
 *   collapses into one paragraph (codex-reviewer, same review).
 */
export function describeSkips(steps: PopulateStep[] | undefined): string {
  const blocks: string[] = [];
  for (const s of steps || []) {
    if (!s?.skipped) continue;

    const gates = s.skipReasons
      ? Object.entries(s.skipReasons)
          .sort((a, b) => b[1] - a[1])
          .map(([gate, n]) => `${gate}=${n}`)
          .join(', ')
      : '';

    const samples = (s.skipSamples || []).map(renderSample).join('\n');

    // ⚠️ State the CAP when it bit. A list that silently stops at 20 reads as "these are all of
    //    them" — the same false completeness this whole surface exists to prevent, one level up.
    const more = s.skipSamplesTruncated && s.skipSamples
      ? `\n    … showing ${s.skipSamples.length} of ${s.skipped}`
        + `${s.skipSampleCap ? ` (cap ${s.skipSampleCap})` : ''}`
      : '';

    blocks.push(
      `  ${s.step} — ${s.skipped} skipped${gates ? ` (${gates})` : ''}`
      + (samples ? `\n${samples}` : '')
      + more,
    );
  }
  return blocks.length ? `\n\nWhy rows were not written:\n${blocks.join('\n')}` : '';
}
